import { ArrowBack } from "@mui/icons-material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina",
  "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

export default function ReminderSubscriptionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    email: "",
    telephone: "",
    reminder: true,
    sms: true,
    emailOptIn: true,
    source: "reminderSubscription",
    periods: ["SEVEN_DAYS_BEFORE", "ONE_DAY_BEFORE", "ELECTION_DAY_MORNING"]
  });

  const isInIframe = window.self !== window.top;
  const embedIdFromPage = searchParams.get("embed_id") || window.sessionStorage.getItem("embedded_partner_id") || "";
  const isEmbeddedFlow = isInIframe || searchParams.get("embed") === "true" || !!embedIdFromPage;


  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle periods checkboxes specifically
    if (name === "periods") {
      const periodValue = value;
      setFormData((prev) => {
        const currentPeriods = Array.isArray(prev.periods) ? prev.periods : [];
        const periods = currentPeriods.includes(periodValue)
          ? currentPeriods.filter(p => p !== periodValue)
          : [...currentPeriods, periodValue];
        return { ...prev, periods };
      });
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiUrl}/election-reminder/opt-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(isEmbeddedFlow && embedIdFromPage ? { "x-embed-id": embedIdFromPage } : {}),
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to subscribe");

      setSuccess(true);
      toast.success("Reminder subscription is successful!")
      setTimeout(() => navigate("/"), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 shadow-lg rounded-2xl max-w-4xl mx-auto mt-10">
       {isInIframe && <div className="flex items-center gap-2 cursor-pointer absolute top-5 left-5  text-sm" onClick={() => window.history.back()}>
        <ArrowBack /> Go back
      </div>}
      <h2 className="text-black text-3xl font-bold text-center mb-6">
        Get Election Reminders
      </h2>

      {success ? (
        <p className="text-green-600 font-semibold text-center">
          You’ve been subscribed successfully!
        </p>
      ) : (
        <form className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" onSubmit={handleSubmit}>
          {/* Date of Birth */}
          <div>
            <label htmlFor="dob" className="block text-sm font-medium mb-1 text-gray-800">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="dob"
              id="dob"
              value={formData.dob}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="Date of Birth"
            />
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium mb-1 text-gray-800">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              id="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="123 Main Street"
            />
          </div>
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium mb-1 text-gray-800">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="First Name"
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium mb-1 text-gray-800">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="Last Name"
            />
          </div>

          {/* State Dropdown */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium mb-1 text-gray-800">
              State <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                name="state"
                id="state"
                value={formData.state}
                onChange={handleChange}
                required
                className="appearance-none w-full border border-gray-300 rounded px-4 py-3 pr-10 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select State</option>
                {states.map((state, idx) => (
                  <option key={idx} value={state}>{state}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12l-6-6h12l-6 6z" />
                </svg>
              </div>
            </div>
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium mb-1 text-gray-800">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              id="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="City"
            />
          </div>

          {/* Telephone */}
          <div>
            <label htmlFor="telephone" className="block text-sm font-medium mb-1 text-gray-800">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="telephone"
              id="telephone"
              value={formData.telephone}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="+1234567890"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1 text-gray-800">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="Email Address"
            />
          </div>

          {/* Zipcode */}
          <div>
            <label htmlFor="zipcode" className="block text-sm font-medium mb-1 text-gray-800">
              Zipcode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="zipcode"
              id="zipcode"
              value={formData.zipcode}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-3 rounded text-gray-900"
              placeholder="Zipcode"
            />
          </div>

          {/* Reminder Preferences */}
          <div className="col-span-full">
            <label className="block text-sm font-medium mb-3 text-gray-800">
              Reminder Preferences <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="reminder"
                  checked={formData.reminder}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Enable election reminders</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="sms"
                  checked={formData.sms}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Receive SMS reminders</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="emailOptIn"
                  checked={formData.emailOptIn}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Receive email reminders</span>
              </label>
            </div>
          </div>

          {/* Reminder Periods */}
          <div className="col-span-full">
            <label className="block text-sm font-medium mb-3 text-gray-800">
              When would you like to receive reminders? <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { value: "SEVEN_DAYS_BEFORE", label: "7 Days Before Election" },
                { value: "ONE_DAY_BEFORE", label: "1 Day Before Election" },
                { value: "ELECTION_DAY_MORNING", label: "Election Day Morning" }
              ].map((period) => (
                <label key={period.value} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    name="periods"
                    value={period.value}
                    checked={Array.isArray(formData.periods) && formData.periods.includes(period.value)}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 text-sm">{period.label}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-red-600 text-sm col-span-full">{error}</p>}

          {/* Disclaimer */}
          <div className="col-span-full text-sm text-gray-700 font-medium mt-2 leading-relaxed">
            <strong>We'll only send reminders related to your state's election dates.</strong>
            <p className="text-xs text-gray-500 mt-2">
              By providing your phone number, you consent to receive text messages. Message and data rates may apply. Reply STOP to unsubscribe.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="col-span-full w-full bg-red-600 text-white py-3 rounded hover:bg-red-700 mt-2 font-semibold"
          >
            {loading ? "Submitting..." : "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
}
